import { Box, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowDurationUnits, FlowHumanTaskAutofillStrategy } from 'flow-schema';

import { Tag, Text, api, getQueryProps } from 'common';

import { FeatureFlagKey, useFlag } from '../../../../../../../../utils/flags';
import { useFlowDefinitionContext } from '../../../../../context';
import { BlockTagsFields } from '../../../ConfigFields/BlockTagsFields';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../../ConfigFields/Duration';
import { BlockRadioGroupField } from '../../../ConfigFields/RadioGroupField';
import { BlockSelectField } from '../../../ConfigFields/SelectField';

export function HumanTaskGeneralFormFields() {
  const { flowDefinition } = useFlowDefinitionContext();
  const sidekickAiAutofillFlag = useFlag({ flagKey: FeatureFlagKey.sidekickAiAutofill });
  const { data: categories = [] } = useQuery({
    ...getQueryProps(api.client.AssignmentGroup, 'findAll', {
      organizationId: flowDefinition.organizationId,
      limit: 500,
    }),
  });

  return (
    <Stack gap={2}>
      <BlockSelectField
        label="Assignment Group"
        fieldName="config.categoryId"
        fieldVariant="SelectOrBlockValueTemplate"
        options={categories?.map((category) => ({
          label: category.name,
          value: category.id,
        }))}
      />
      <BlockDurationField
        variant="BlockValueTemplate"
        fieldName="config.expirationDuration"
        unitFieldNameSuffix="units"
        required
        allowedUnits={[
          FlowDurationUnits.Weeks,
          FlowDurationUnits.Days,
          FlowDurationUnits.Hours,
          FlowDurationUnits.Minutes,
        ]}
        valueLabel="Expiration Duration"
        valueHelperText="Task will expire after this duration"
      />
      <BlockValueTemplateField
        fieldName="config.externalId"
        label="External Task ID (optional)"
        helperText="Must resolve to a unique UUID for each block run"
      />
      <BlockTagsFields helperText="Must start with a letter. Only letters, numbers, underscores, and hyphens allowed." />
      {sidekickAiAutofillFlag.isEnabled && (
        <BlockRadioGroupField
          label="Autofill Strategy"
          fieldName="config.autofillStrategy"
          defaultValue={FlowHumanTaskAutofillStrategy.Scripted}
          options={[
            {
              label: (
                <Box>
                  <Text variant="body2" bold>
                    Standard
                  </Text>
                  <Text variant="subtitle2">Scripted sequence of actions per portal</Text>
                </Box>
              ),
              value: FlowHumanTaskAutofillStrategy.Scripted,
            },
            {
              label: (
                <Box>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Text variant="body2" bold>
                      AI Mode
                    </Text>
                    <Tag color="warning">Experimental</Tag>
                  </Stack>
                  <Text variant="subtitle2">AI-driven dynamic completion of portal fields</Text>
                </Box>
              ),
              value: FlowHumanTaskAutofillStrategy.AiAssisted,
            },
          ]}
        />
      )}
    </Stack>
  );
}
